import React, { useEffect, useState } from 'react';
import {
  Card, Form, Input, Switch, Button, Space,
  message, Alert, Divider, Select, Badge, Descriptions
} from 'antd';
import {
  CloudOutlined, SaveOutlined, CheckCircleOutlined,
  CloseCircleOutlined, InfoCircleOutlined, ReloadOutlined
} from '@ant-design/icons';

const { TextArea } = Input;
const { Option } = Select;

const StorageConfigManager: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [configForm] = Form.useForm();

  // 默认存储配置
  const defaultStorageConfig = {
    // 腾讯云COS
    tencentCOSEnabled: false,
    tencentSecretId: '',
    tencentSecretKey: '',
    tencentBucket: '',
    tencentRegion: 'ap-guangzhou',
    tencentDomain: '',

    // Cloudflare R2
    cloudflareR2Enabled: true,
    cloudflareAccountId: '',
    cloudflareAccessKeyId: '',
    cloudflareSecretAccessKey: '',
    cloudflareBucket: '',
    cloudflarePublicDomain: '',

    // 优先级设置
    storagePriority: 'cloudflare', // 'cloudflare' | 'tencent' | 'local'
  };

  // 加载已保存的存储配置
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { apiClient } = await import('@/services/api');
        const resp = await apiClient.get('/admin/storage/config');
        if (resp?.success && mounted) {
          const data = resp.data || {};
          configForm.setFieldsValue({
            ...defaultStorageConfig,
            ...data,
          });
        }
      } catch (_) {
        // 忽略加载失败，使用默认值
        configForm.setFieldsValue({ ...defaultStorageConfig });
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // 保存存储配置
  const handleSaveConfig = async (values: any) => {
    try {
      setLoading(true);
      console.log('💾 保存存储配置:', values);
      const { apiClient } = await import('@/services/api');
      const resp = await apiClient.post('/admin/storage/config', values);
      if (!resp?.success) throw new Error(resp?.message || '保存失败');
      console.log('✅ 存储配置保存成功:', resp);
      message.success('存储配置已保存');
    } catch (error: any) {
      console.error('❌ 保存失败:', error);
      message.error('保存失败: ' + (error?.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 测试存储连接
  const handleTestStorage = async (provider: 'cloudflare' | 'tencent') => {
    try {
      setLoading(true);
      const { apiClient } = await import('@/services/api');
      const resp = await apiClient.get(`/admin/storage/test/${provider}`);
      if (resp?.success && resp.data?.available) {
        message.success(`${provider === 'cloudflare' ? 'Cloudflare R2' : '腾讯云COS'} 连接成功！`);
      } else {
        message.error(resp.data?.message || '连接失败');
      }
    } catch (error: any) {
      message.error('测试失败: ' + (error?.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center space-x-3">
        <CloudOutlined className="text-2xl text-blue-600" />
        <h2 className="text-2xl font-bold text-gray-800">云存储配置</h2>
      </div>

      <Alert
        message="云存储配置说明"
        description="配置云存储服务用于保存生成的视频和图像。建议优先使用Cloudflare R2（零出口费用），国内用户可选择腾讯云COS。"
        type="info"
        showIcon
        closable
      />

      {/* 存储配置 */}
      <Card className="shadow-md">
        <Form
          form={configForm}
          layout="vertical"
          onFinish={handleSaveConfig}
          initialValues={defaultStorageConfig}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Cloudflare R2 配置 */}
            <Card 
              title={
                <div className="flex items-center justify-between">
                  <span>☁️ Cloudflare R2（推荐）</span>
                  <a 
                    href="https://developers.cloudflare.com/r2/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-blue-500 hover:text-blue-700"
                    onClick={(e) => e.stopPropagation()}
                  >
                    📖 配置文档
                  </a>
                </div>
              }
              size="small" 
              className="shadow-sm"
            >
              <Alert
                message={
                  <div>
                    <div>零出口费用，全球CDN加速</div>
                    <a 
                      href="https://dash.cloudflare.com/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline"
                    >
                      → 前往Cloudflare控制台获取Account ID
                    </a>
                  </div>
                }
                type="success"
                showIcon
                className="mb-4"
              />
              <Form.Item
                label="启用 Cloudflare R2"
                name="cloudflareR2Enabled"
                valuePropName="checked"
              >
                <Switch checkedChildren="开启" unCheckedChildren="关闭" defaultChecked />
              </Form.Item>
              <Form.Item
                label={
                  <div className="flex items-center gap-2">
                    <span>Account ID</span>
                    <a 
                      href="https://dash.cloudflare.com/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-500 hover:underline"
                      title="点击前往获取Account ID"
                    >
                      <InfoCircleOutlined /> 如何获取?
                    </a>
                  </div>
                }
                name="cloudflareAccountId"
                extra="在Cloudflare Dashboard右上角，点击账户名获取（32位字符串）"
              >
                <Input placeholder="a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6（32位）" />
              </Form.Item>
              <Form.Item
                label={
                  <div className="flex items-center gap-2">
                    <span>Access Key ID</span>
                    <a 
                      href="https://dash.cloudflare.com/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-500 hover:underline"
                      title="前往创建API令牌"
                    >
                      <InfoCircleOutlined /> 创建令牌
                    </a>
                  </div>
                }
                name="cloudflareAccessKeyId"
                extra="在R2 → 管理R2 API令牌 → 创建API令牌"
              >
                <Input placeholder="访问密钥ID（20位）" />
              </Form.Item>
              <Form.Item
                label="Secret Access Key"
                name="cloudflareSecretAccessKey"
                extra="⚠️ 只显示一次，请立即保存"
              >
                <Input.Password placeholder="访问密钥（40位）" />
              </Form.Item>
              <Form.Item
                label="Bucket 名称"
                name="cloudflareBucket"
                extra="在R2控制台创建的存储桶名称"
              >
                <Input placeholder="your-bucket（小写字母+数字+连字符）" />
              </Form.Item>
              <Form.Item
                label="公共域名（可选）"
                name="cloudflarePublicDomain"
              >
                <Input placeholder="https://your-bucket.r2.dev" />
              </Form.Item>
              <Button
                type="dashed"
                icon={<CheckCircleOutlined />}
                onClick={() => handleTestStorage('cloudflare')}
                loading={loading}
                block
              >
                测试 R2 连接
              </Button>
            </Card>

            {/* 腾讯云COS配置 */}
            <Card 
              title={
                <div className="flex items-center justify-between">
                  <span>☁️ 腾讯云COS</span>
                  <a 
                    href="https://cloud.tencent.com/document/product/436" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-blue-500 hover:text-blue-700"
                    onClick={(e) => e.stopPropagation()}
                  >
                    📖 配置文档
                  </a>
                </div>
              }
              size="small" 
              className="shadow-sm"
            >
              <Alert
                message={
                  <div>
                    <div>国内访问速度快，CDN加速</div>
                    <a 
                      href="https://console.cloud.tencent.com/cos" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline"
                    >
                      → 前往腾讯云COS控制台
                    </a>
                  </div>
                }
                type="info"
                showIcon
                className="mb-4"
              />
              <Form.Item
                label="启用腾讯云COS"
                name="tencentCOSEnabled"
                valuePropName="checked"
              >
                <Switch checkedChildren="开启" unCheckedChildren="关闭" />
              </Form.Item>
              <Form.Item
                label={
                  <div className="flex items-center gap-2">
                    <span>Secret ID</span>
                    <a 
                      href="https://console.cloud.tencent.com/cam/capi" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-500 hover:underline"
                      title="前往获取API密钥"
                    >
                      <InfoCircleOutlined /> 获取密钥
                    </a>
                  </div>
                }
                name="tencentSecretId"
                extra="访问管理 → API密钥管理 → 新建密钥"
              >
                <Input placeholder="AKID...（以AKID开头）" />
              </Form.Item>
              <Form.Item
                label="Secret Key"
                name="tencentSecretKey"
                extra="⚠️ 创建时显示，请立即保存"
              >
                <Input.Password placeholder="密钥（32-40位）" />
              </Form.Item>
              <Form.Item
                label="Bucket 名称"
                name="tencentBucket"
                extra={
                  <div>
                    在COS控制台创建的存储桶名称（格式：名称-APPID）
                    <a 
                      href="https://console.cloud.tencent.com/cos/bucket" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="ml-2 text-blue-500 hover:underline"
                    >
                      前往创建存储桶
                    </a>
                  </div>
                }
              >
                <Input placeholder="your-bucket-1234567890" />
              </Form.Item>
              <Form.Item
                label="地域"
                name="tencentRegion"
              >
                <Select>
                  <Option value="ap-guangzhou">广州</Option>
                  <Option value="ap-shanghai">上海</Option>
                  <Option value="ap-beijing">北京</Option>
                  <Option value="ap-chengdu">成都</Option>
                  <Option value="ap-chongqing">重庆</Option>
                  <Option value="ap-hongkong">香港</Option>
                  <Option value="ap-singapore">新加坡</Option>
                </Select>
              </Form.Item>
              <Form.Item
                label="自定义域名（可选）"
                name="tencentDomain"
              >
                <Input placeholder="https://cdn.your-domain.com" />
              </Form.Item>
              <Button
                type="dashed"
                icon={<CheckCircleOutlined />}
                onClick={() => handleTestStorage('tencent')}
                loading={loading}
                block
              >
                测试 COS 连接
              </Button>
            </Card>
          </div>

          <Divider />

          {/* 优先级设置 */}
          <Card title="存储优先级" size="small" className="shadow-sm mb-6">
            <Form.Item
              label="首选存储服务"
              name="storagePriority"
              help="当首选服务不可用时，系统将自动切换到备用服务"
            >
              <Select>
                <Option value="cloudflare">Cloudflare R2（推荐）</Option>
                <Option value="tencent">腾讯云COS</Option>
                <Option value="local">本地存储（仅开发）</Option>
              </Select>
            </Form.Item>
            
            <Alert
              message="降级策略"
              description={
                <div className="space-y-1">
                  <div>1️⃣ 首选服务（上面配置）</div>
                  <div>2️⃣ 备用服务（自动切换）</div>
                  <div>3️⃣ 本地存储（最后备用）</div>
                </div>
              }
              type="info"
              showIcon
            />
          </Card>

          <div className="flex justify-center space-x-4">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => configForm.resetFields()}
            >
              重置
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SaveOutlined />}
              size="large"
              className="px-8"
            >
              保存配置
            </Button>
          </div>
        </Form>
      </Card>

      {/* 存储使用说明 */}
      <Card title="📖 使用说明" className="shadow-md">
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="Cloudflare R2">
            <div>
              <div>✅ 零出口费用（免费流量）</div>
              <div>✅ 全球CDN加速</div>
              <div>✅ S3兼容API</div>
              <div>💰 存储：$0.015/GB/月</div>
              <div>📖 <a href="https://developers.cloudflare.com/r2/" target="_blank" rel="noopener noreferrer">查看文档</a></div>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="腾讯云COS">
            <div>
              <div>✅ 国内访问速度快</div>
              <div>✅ CDN加速支持</div>
              <div>✅ 稳定可靠</div>
              <div>💰 存储：¥0.099/GB/月</div>
              <div>💰 流量：¥0.50/GB</div>
              <div>📖 <a href="https://cloud.tencent.com/document/product/436" target="_blank" rel="noopener noreferrer">查看文档</a></div>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="本地存储">
            <div>
              <div>⚠️ 仅用于开发环境</div>
              <div>⚠️ 不适合生产环境</div>
              <div>⚠️ 无CDN加速</div>
              <div>💰 免费</div>
            </div>
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
};

export default StorageConfigManager;

